"use client";

import { createLogger } from "@/lib/logger";
import { useCallback, useEffect, useMemo, useState } from "react";

const logger = createLogger("background-video");

const STORAGE_KEY = "chicky-background-video";
const VIDEO_CHANGE_EVENT = "chicky-background-video-change";
const DEFAULT_BACKGROUND_VIDEO_POSTER = "/ui/chicky-background-default-poster.webp";
export const ECOLE_DEFAULT_VIDEO = "/ui/ecole-ping-pong.mp4";
export const ECOLE_DEFAULT_POSTER = "/ui/ecole-ping-pong-poster.webp";

export interface BackgroundVideoOption {
  id: string;
  label: string;
  url: string;
  posterUrl?: string;
}

export const PRESET_VIDEOS: BackgroundVideoOption[] = [
  {
    id: "none",
    label: "Plain Black",
    url: "", // Empty URL means no video
  },
  {
    id: "local-reverse",
    label: "Chicky Legacy",
    url: "/ui/reverse.mp4",
  },
  {
    id: "pixabay-abstract-1",
    label: "Orange Dancer",
    url: "https://cdn.pixabay.com/video/2023/10/20/185787-876545918_large.mp4",
  },
  {
    id: "pixabay-abstract-2",
    label: "Chicky Default",
    url: "https://cdn.pixabay.com/video/2023/05/30/165148-832090850_tiny.mp4",
    posterUrl: DEFAULT_BACKGROUND_VIDEO_POSTER,
  },
  {
    id: "ecole-default",
    label: "Ecole Default",
    url: ECOLE_DEFAULT_VIDEO,
    posterUrl: ECOLE_DEFAULT_POSTER,
  },
  {
    id: "pixabay-particles",
    label: "Chicky Egg",
    url: "https://cdn.pixabay.com/video/2019/12/06/29874-377759949_tiny.mp4",
  },
  {
    id: "pixabay-gradient",
    label: "LoFi Studying",
    url: "https://cdn.pixabay.com/video/2025/04/13/271841_tiny.mp4",
  },
];

export interface BackgroundVideoState {
  selectedId: string;
  customUrl: string | null;
}

// Helper to get video URL from state
function getVideoUrlFromState(state: BackgroundVideoState): string {
  if (state.selectedId === "custom" && state.customUrl) {
    return state.customUrl;
  }
  const preset = PRESET_VIDEOS.find((v) => v.id === state.selectedId);
  // Return the preset URL (even if empty for "none" option), or fall back to default
  if (preset) {
    return preset.url; // Can be empty string for "none"
  }
  return "";
}

function getPosterUrlFromState(state: BackgroundVideoState): string | null {
  if (state.selectedId === "custom") {
    return null;
  }
  const preset = PRESET_VIDEOS.find((v) => v.id === state.selectedId);
  return preset?.posterUrl ?? null;
}

// Fresh users default to the Ecole background. Existing stored selections are
// preserved and continue to override this fallback.
const DEFAULT_STATE: BackgroundVideoState = {
  selectedId: "ecole-default",
  customUrl: null,
};

interface UseBackgroundVideoOptions {
  defaultState?: BackgroundVideoState;
  fallbackVideoUrl?: string;
  fallbackPosterUrl?: string | null;
}

export function useBackgroundVideo(options: UseBackgroundVideoOptions = {}) {
  const defaultSelectedId = options.defaultState?.selectedId ?? DEFAULT_STATE.selectedId;
  const defaultCustomUrl = options.defaultState?.customUrl ?? DEFAULT_STATE.customUrl;
  const defaultState = useMemo<BackgroundVideoState>(
    () => ({
      selectedId: defaultSelectedId,
      customUrl: defaultCustomUrl,
    }),
    [defaultSelectedId, defaultCustomUrl]
  );
  const [mounted, setMounted] = useState(false);
  const [state, setState] = useState<BackgroundVideoState>(defaultState);
  const [hasStoredPreference, setHasStoredPreference] = useState(false);

  // Load from localStorage on mount and listen for changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Load initial state
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setState(parsed);
        setHasStoredPreference(true);
      } else {
        setState(defaultState);
        setHasStoredPreference(false);
      }
    } catch (error) {
      logger.error("Error loading background video settings", {
        error: error instanceof Error ? error.message : String(error),
      });
      setState(defaultState);
      setHasStoredPreference(false);
    }
    setMounted(true);

    // Listen for changes from other instances of the hook
    const handleChange = (event: CustomEvent<BackgroundVideoState>) => {
      setState(event.detail);
      setHasStoredPreference(true);
    };

    window.addEventListener(VIDEO_CHANGE_EVENT, handleChange as EventListener);
    return () => {
      window.removeEventListener(VIDEO_CHANGE_EVENT, handleChange as EventListener);
    };
  }, [defaultState]);

  // Save to localStorage and broadcast change
  const saveState = useCallback((newState: BackgroundVideoState) => {
    setState(newState);
    setHasStoredPreference(true);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        // Broadcast change to other instances
        window.dispatchEvent(new CustomEvent(VIDEO_CHANGE_EVENT, { detail: newState }));
      } catch (error) {
        logger.error("Error saving background video settings", {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }, []);

  // Select a preset video
  const selectPreset = useCallback(
    (id: string) => {
      saveState({
        selectedId: id,
        customUrl: id === "custom" ? state.customUrl : null,
      });
    },
    [saveState, state.customUrl]
  );

  // Set a custom video URL
  const setCustomUrl = useCallback(
    (url: string) => {
      saveState({
        selectedId: "custom",
        customUrl: url,
      });
    },
    [saveState]
  );

  const fallbackVideoUrl = options.fallbackVideoUrl ?? getVideoUrlFromState(defaultState);
  const fallbackPosterUrl =
    options.fallbackPosterUrl === undefined
      ? getPosterUrlFromState(defaultState)
      : options.fallbackPosterUrl;
  const videoUrl = mounted
    ? hasStoredPreference
      ? getVideoUrlFromState(state)
      : fallbackVideoUrl
    : fallbackVideoUrl;
  const posterUrl = mounted
    ? hasStoredPreference
      ? getPosterUrlFromState(state)
      : fallbackPosterUrl
    : fallbackPosterUrl;

  return {
    mounted,
    state,
    presets: PRESET_VIDEOS,
    selectPreset,
    setCustomUrl,
    videoUrl,
    posterUrl,
  };
}
